import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronRight } from 'lucide-react';
import { thClass, tdClass, STATUS_LABELS, euro } from './adminUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Statuts acceptés par le back-end (services/orders/handler.py).
const ORDER_STATUSES = ['pending', 'paid', 'shipped', 'delivered', 'cancelled'];

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const formatAddress = (addr) => {
  if (!addr) return '—';
  if (typeof addr === 'string') return addr;
  const lines = [
    [addr.firstName, addr.lastName].filter(Boolean).join(' '),
    addr.address,
    [addr.postalCode, addr.city].filter(Boolean).join(' '),
    addr.country,
    addr.phone,
  ].filter(Boolean);
  return lines.length ? lines.join('\n') : '—';
};

const clientLabel = (order) =>
  order.guest_email || (order.user_id ? `Client inscrit (#${order.user_id.slice(0, 8)})` : '—');

const AdminOrders = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () =>
    axios
      .get(`${API}/admin/orders`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : r.data.orders || []))
      .catch(() => toast.error('Échec du chargement des commandes'))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status });
      toast.success(`Commande : ${STATUS_LABELS[status] || status}`);
      load();
      if (active?.id === orderId) {
        setActive((prev) => (prev ? { ...prev, status } : prev));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  return (
    <div data-testid="admin-orders-list" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <h2 className="font-heading text-2xl mb-6">Commandes ({items.length})</h2>
        {loading ? (
          <p className="text-foreground/40">Chargement…</p>
        ) : items.length === 0 ? (
          <p className="text-foreground/40 py-8">Aucune commande pour le moment.</p>
        ) : (
          <div className="overflow-x-auto border border-black/10 max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-background">
                <tr>
                  <th className={thClass}>Date</th>
                  <th className={thClass}>N°</th>
                  <th className={thClass}>Client</th>
                  <th className={thClass}>Total</th>
                  <th className={thClass}>Statut</th>
                  <th className={thClass} aria-hidden />
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => setActive(o)}
                    className={`cursor-pointer hover:bg-black/[0.03] transition-colors ${active?.id === o.id ? 'bg-black/5' : ''}`}
                    data-testid={`admin-order-row-${o.id}`}
                  >
                    <td className={`${tdClass} whitespace-nowrap text-xs`}>{formatDate(o.created_at)}</td>
                    <td className={`${tdClass} font-mono text-xs`}>#{o.id.slice(0, 8)}</td>
                    <td className={`${tdClass} text-xs max-w-[140px] truncate`}>{clientLabel(o)}</td>
                    <td className={`${tdClass} text-gold whitespace-nowrap`}>{euro(o.total)}</td>
                    <td className={tdClass}>
                      <span className="text-xs uppercase tracking-wider">{STATUS_LABELS[o.status] || o.status}</span>
                    </td>
                    <td className={tdClass}>
                      <ChevronRight size={16} className="text-foreground/30" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="font-heading text-2xl mb-6">Détail</h2>
        {!active ? (
          <p className="text-foreground/40 py-8">Sélectionnez une commande pour voir les détails.</p>
        ) : (
          <div className="border border-black/10 p-5 space-y-6" data-testid={`admin-order-detail-${active.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs text-foreground/40 uppercase tracking-wider">Commande</p>
                <p className="font-mono text-sm mt-1">{active.id}</p>
                <p className="text-sm text-foreground/60 mt-2">{formatDate(active.created_at)}</p>
              </div>
              <div className="text-right">
                <p className="price-tag text-gold text-2xl">{euro(active.total)}</p>
                <p className="text-xs text-foreground/40 mt-1">{active.items?.length || 0} article(s)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs uppercase tracking-widest text-foreground/40 mb-2">Client</p>
                <p className="text-foreground/80">{clientLabel(active)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-foreground/40 mb-2">Paiement</p>
                <p className="text-foreground/80 capitalize">{active.payment_method || '—'}</p>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-foreground/40 mb-2">Adresse de livraison</p>
              <p className="text-sm text-foreground/80 whitespace-pre-line">{formatAddress(active.shipping_address)}</p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-foreground/40 mb-3">Articles</p>
              <div className="border border-black/10 divide-y divide-black/10">
                {(active.items || []).map((item, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 p-3 text-sm">
                    <div className="min-w-0">
                      <p className="text-foreground/90 truncate">{item.name}</p>
                      <p className="text-xs text-foreground/40 mt-0.5">
                        {[item.size, item.color].filter(Boolean).join(' · ') || '—'}
                        {' · '}Qté {item.quantity}
                      </p>
                    </div>
                    <p className="text-gold whitespace-nowrap">{euro(item.item_total ?? item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-foreground/40 mb-2 block">Statut</label>
              <select
                value={active.status}
                onChange={(e) => updateStatus(active.id, e.target.value)}
                className="w-full bg-transparent border border-black/15 text-foreground text-sm px-3 py-2.5 focus:border-gold focus:outline-none"
                data-testid={`admin-order-status-${active.id}`}
              >
                {ORDER_STATUSES.map((s) => (
                  <option key={s} value={s} className="bg-background">
                    {STATUS_LABELS[s] || s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
